/* Discover meals — tabbed product carousel (Figma 12136:966).
   - <flickity-meal-carousel>: breakpoint-aware Flickity with custom side arrows.
     It does not loop: running past its first/last page fires
     `meal-carousel:edge` ({ step: -1 | 1 }) so the tabs can move on.
   - <meal-tabs>: swaps the visible carousel panel and re-sizes the revealed one,
     points the section CTA at the active tab's Category filter panel, and
     hands off to the neighbouring tab on `meal-carousel:edge`. */

// How far a swipe must push past the first/last page to count as "next tab".
const EDGE_SWIPE_PX = 60;

if (!customElements.get("flickity-meal-carousel")) {
  customElements.define(
    "flickity-meal-carousel",
    class FlickityMealCarousel extends window.FlickityCarouselBase {
      connectedCallback() {
        super.connectedCallback();
        this._wireArrows();
      }

      // Re-size on visibility change (a hidden tab's carousel has offsetWidth 0
      // at init; the ResizeObserver fires when its panel is revealed).
      observeResize() {
        return true;
      }

      _cellsPerPage(mode) {
        if (mode === "mobile") return 2;
        const n = parseInt(this.dataset.cellsPerSlideDesktop, 10);
        return n === 3 ? 3 : 4;
      }

      _cells() {
        return Array.from(this.querySelectorAll(".discover-meals__cell"));
      }

      _setCellPixelWidths(mode) {
        const sliderWidth = this.offsetWidth;
        if (!sliderWidth) return;
        const perPage = this._cellsPerPage(mode);
        const cellWidth = sliderWidth / perPage;
        this._cells().forEach((c) => {
          c.style.width = `${cellWidth}px`;
          c.style.flexBasis = `${cellWidth}px`;
        });
      }

      beforeInit(mode) {
        this._setCellPixelWidths(mode);
      }

      getFlickityOptions(mode) {
        return {
          cellSelector: ".discover-meals__cell",
          cellAlign: "left",
          contain: true,
          pageDots: mode === "mobile",
          prevNextButtons: false,
          groupCells: this._cellsPerPage(mode),
          freeScroll: false,
          // No looping: the last page hands off to the next tab instead
          // (Meals -> Breakfast -> Snacks), via meal-carousel:edge.
          wrapAround: false,
          dragThreshold: 10,
          accessibility: true,
          // Off: imagesLoaded proxies every <img> through `new Image()`, which
          // downloads lazy and hidden-tab images up front. Media boxes have fixed
          // aspect ratios, so cell heights never depend on the images.
          imagesLoaded: false,
        };
      }

      afterInit() {
        this.syncArrows();
        this._positionArrows();
        this.flickity.on("select", () => this.syncArrows());
        this._watchEdgeSwipe();
      }

      onResize(mode) {
        this._setCellPixelWidths(mode);
        this.syncArrows();
        this._positionArrows();
      }

      _isFirst() {
        return this.flickity.selectedIndex === 0;
      }

      _isLast() {
        return this.flickity.selectedIndex === this.flickity.slides.length - 1;
      }

      // Arrow press: page within the tab, or hand off once at either end.
      _step(step) {
        if (!this.flickity) return;
        const atEdge = step > 0 ? this._isLast() : this._isFirst();
        if (atEdge) this._handOff(step);
        else if (step > 0) this.flickity.next();
        else this.flickity.previous();
      }

      _handOff(step) {
        this.dispatchEvent(
          new CustomEvent("meal-carousel:edge", { bubbles: true, detail: { step } }),
        );
      }

      // Without wrapAround, a swipe past the first/last page just springs back,
      // so it shows up as a drag that ends on the page it started from.
      _watchEdgeSwipe() {
        let startIndex = 0;
        let dx = 0;
        this.flickity.on("dragStart", () => {
          startIndex = this.flickity.selectedIndex;
          dx = 0;
        });
        this.flickity.on("dragMove", (_event, _pointer, moveVector) => {
          dx = moveVector.x;
        });
        this.flickity.on("dragEnd", () => {
          if (this.flickity.selectedIndex !== startIndex) return;
          if (dx < -EDGE_SWIPE_PX && this._isLast()) this._handOff(1);
          else if (dx > EDGE_SWIPE_PX && this._isFirst()) this._handOff(-1);
        });
      }

      // Center the side arrows on the card image (not the whole card), matching
      // the Figma design. Image height is dynamic (cell width / aspect ratio).
      _positionArrows() {
        const media = this.querySelector(".discover-meal-card__media");
        const panel = this.closest("[data-panel]");
        if (media && panel && media.offsetHeight) {
          panel.style.setProperty("--arrow-center", `${media.offsetHeight / 2}px`);
        }
      }

      _wireArrows() {
        const panel = this.closest("[data-panel]");
        this._prevBtn = panel?.querySelector("[data-carousel-prev]");
        this._nextBtn = panel?.querySelector("[data-carousel-next]");
        this._prevBtn?.addEventListener("click", () => this._step(-1));
        this._nextBtn?.addEventListener("click", () => this._step(1));
      }

      // Public: <meal-tabs> calls it after marking which ends can hand off.
      syncArrows() {
        if (!this.flickity || !this._prevBtn || !this._nextBtn) return;
        // An end page is only a dead end when there is no tab to hand off to
        // (data-handoff-* is set on the panel by <meal-tabs>).
        const panel = this.closest("[data-panel]");
        this._prevBtn.disabled = this._isFirst() && panel?.dataset.handoffPrev !== "true";
        this._nextBtn.disabled = this._isLast() && panel?.dataset.handoffNext !== "true";
      }
    },
  );
}

if (!customElements.get("meal-tabs")) {
  customElements.define(
    "meal-tabs",
    class MealTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll("[data-tab]"));
        this.panels = Array.from(this.querySelectorAll("[data-panel]"));
        // The CTA sits outside this element, so scope the lookup to the section.
        this.cta = this.closest(".discover-meals")?.querySelector("[data-discover-cta]");
        this._onClick = this._onClick.bind(this);
        this._onKeydown = this._onKeydown.bind(this);
        this._onEdge = this._onEdge.bind(this);
        this.tabs.forEach((tab) => {
          tab.addEventListener("click", this._onClick);
          tab.addEventListener("keydown", this._onKeydown);
        });
        this.addEventListener("meal-carousel:edge", this._onEdge);
        this._markHandoffs();
      }

      disconnectedCallback() {
        this.tabs?.forEach((tab) => {
          tab.removeEventListener("click", this._onClick);
          tab.removeEventListener("keydown", this._onKeydown);
        });
        this.removeEventListener("meal-carousel:edge", this._onEdge);
      }

      // Which tab a carousel hands off to when it runs past its last page
      // (step 1) or its first page (step -1): the next one along, wrapping
      // round at the ends (Extras -> Meals) so the browse never dead-ends -
      // the same endless feel the carousel had when it looped on its own.
      // Tabs with no meals are skipped, as their panel has no carousel to land
      // on. Returns null only when no other tab has meals.
      _neighbourTab(index, step) {
        const count = this.tabs.length;
        for (let n = 1; n < count; n++) {
          const tab = this.tabs[(((index + step * n) % count) + count) % count];
          if (this._panelFor(tab)?.querySelector("flickity-meal-carousel")) return tab;
        }
        return null;
      }

      _panelFor(tab) {
        const target = tab.getAttribute("data-tab");
        return this.panels.find((p) => p.getAttribute("data-panel") === target);
      }

      // Tell each panel whether its ends can hand off, so the carousel only
      // disables an arrow at a true dead end.
      _markHandoffs() {
        this.tabs.forEach((tab, i) => {
          const panel = this._panelFor(tab);
          if (!panel) return;
          panel.dataset.handoffPrev = String(!!this._neighbourTab(i, -1));
          panel.dataset.handoffNext = String(!!this._neighbourTab(i, 1));
          panel.querySelector("flickity-meal-carousel")?.syncArrows?.();
        });
      }

      _onEdge(e) {
        const { step } = e.detail;
        const current = this.tabs.findIndex((t) => t.getAttribute("aria-selected") === "true");
        const next = this._neighbourTab(current, step);
        if (!next) return;

        // Arrow presses keep focus: move it to the same arrow in the new panel,
        // or it would be stranded on a button that has just been hidden.
        const hadFocus = e.target.closest("[data-panel]")?.contains(document.activeElement);

        // Forwards opens the next tab at its start; backwards at its end, so
        // "previous" reads as stepping back through one continuous list.
        this._activate(next, { startAt: step > 0 ? "start" : "end" });

        if (hadFocus) {
          const arrow = step > 0 ? "[data-carousel-next]" : "[data-carousel-prev]";
          this._panelFor(next)?.querySelector(arrow)?.focus();
        }
      }

      _activate(tab, { startAt } = {}) {
        const target = tab.getAttribute("data-tab");

        this._syncCta(tab);

        this.tabs.forEach((t) => {
          const active = t === tab;
          t.classList.toggle("is-active", active);
          t.setAttribute("aria-selected", active ? "true" : "false");
          t.tabIndex = active ? 0 : -1;
        });

        this.panels.forEach((panel) => {
          const active = panel.getAttribute("data-panel") === target;
          panel.hidden = !active;
          if (active) {
            const carousel = panel.querySelector("flickity-meal-carousel");
            // Wait a frame so layout settles before Flickity recalculates.
            requestAnimationFrame(() => {
              const flkty = carousel?.flickity;
              if (!flkty) return;
              flkty.resize();
              if (startAt) {
                const index = startAt === "end" ? flkty.slides.length - 1 : 0;
                flkty.select(index, false, true); // instant: no slide-in from elsewhere
              }
            });
          }
        });
      }

      // "See Full Menu" follows the selected tab: its own link, else its Category
      // filter anchor (both resolved in Liquid), else the section fallback.
      _syncCta(tab) {
        if (!this.cta) return;
        const url = tab.dataset.ctaUrl || this.cta.dataset.fallbackUrl;
        if (url) this.cta.href = url;
      }

      _onClick(e) {
        this._activate(e.currentTarget);
      }

      _onKeydown(e) {
        const i = this.tabs.indexOf(e.currentTarget);
        if (i === -1) return;
        let next = null;
        if (e.key === "ArrowRight" || e.key === "ArrowDown") next = this.tabs[i + 1] || this.tabs[0];
        else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = this.tabs[i - 1] || this.tabs[this.tabs.length - 1];
        if (!next) return;
        e.preventDefault();
        this._activate(next);
        next.focus();
      }
    },
  );
}
